import { useCallback } from 'react';
import { calculateCaretOffsetFromClick } from '../../../shared/utils/caretPositioning';
import { useCaretPositioning } from '../../../shared/hooks/useCaretPositioning';
import { useTaskTextInput } from './useTaskTextInput';
import { useTaskPaste } from './useTaskPaste';
import { useTaskKeyboardNavigation } from './useTaskKeyboardNavigation';

/**
 * Main hook that composes all task text editing handlers
 * @param {Object} params - Configuration object
 * @param {Array} params.tasks - All tasks
 * @param {Set} params.expandedTasks - Set of expanded task IDs
 * @param {Function} params.setExpandedTasks - Function to set expanded tasks
 * @param {string|null} params.editingTaskId - Currently editing task ID
 * @param {Function} params.setEditingTaskId - Function to set editing task ID
 * @param {Object} params.editingElementRef - Ref to the editing element
 * @param {Function} params.updateTaskText - Function to update task text
 * @param {Function} params.updateTaskTextLocal - Function to update task text locally
 * @param {Function} params.updateTaskStatus - Function to update task status
 * @param {Function} params.updateTaskDueDate - Function to update task due date
 * @param {Function} params.updateTaskOrder - Function to update a single task order
 * @param {Function} params.updateTaskOrders - Function to batch update task orders
 * @param {Function} params.updateTaskParentWithTextAndOrder - Function to update task parent and order
 * @param {Function} params.createTask - Function to create a new task
 * @param {Function} params.deleteTask - Function to delete a task
 * @param {Object} params.caretAtZeroTaskIdsRef - Ref to track tasks with caret at zero
 * @param {Function} params.focusFileName - Function to focus file name
 * @param {Object} params.pendingFocusRef - Ref for pending focus (shared with useTaskDeletion)
 * @param {Object} params.placeholderStatusRef - Ref to current placeholder checkbox status (when no tasks)
 * @param {Object} params.placeholderDueDateRef - Ref to current placeholder due date (when no tasks)
 * @param {Object} params.placeholderIsLaterDateRef - Ref to whether placeholder due date is "Later" (when no tasks)
 * @param {Object} params.skipPlaceholderCaretToStartRef - When true, placeholder focus handler will not move caret to start (e.g. after due date set)
 */
export function useTaskHandlers({
  tasks,
  expandedTasks,
  setExpandedTasks,
  editingTaskId,
  setEditingTaskId,
  editingElementRef,
  updateTaskText,
  updateTaskTextLocal,
  updateTaskStatus,
  updateTaskDueDate,
  updateTaskOrder,
  updateTaskOrders,
  updateTaskParentWithTextAndOrder,
  createTask,
  deleteTask,
  caretAtZeroTaskIdsRef,
  focusFileName,
  pendingFocusRef,
  placeholderStatusRef,
  placeholderDueDateRef,
  placeholderIsLaterDateRef,
  skipPlaceholderCaretToStartRef,
  filter,
  visiblePrioritizeSections
}) {
  // Caret positioning hook
  const {
    clickCaretPositionRef,
    arrowKeyCaretPositionRef,
    tabCaretPositionRef
  } = useCaretPositioning({
    editingTaskId,
    tasks,
    editingElementRef
  });

  // Text input hook
  const {
    handleTaskTextInput,
    handleTaskTextFocus,
    handleTaskTextBlur
  } = useTaskTextInput({
    tasks,
    setEditingTaskId,
    editingElementRef,
    updateTaskText,
    updateTaskTextLocal,
    updateTaskDueDate,
    createTask,
    placeholderStatusRef,
    placeholderDueDateRef,
    placeholderIsLaterDateRef,
    skipPlaceholderCaretToStartRef
  });

  // Paste hook
  const {
    handleTaskTextPaste
  } = useTaskPaste({
    tasks,
    setEditingTaskId,
    updateTaskText,
    updateTaskStatus,
    updateTaskDueDate,
    createTask
  });

  // Keyboard navigation hook
  const {
    handleTaskTextKeyDown,
    tryFocusPending,
    focusRequestId
  } = useTaskKeyboardNavigation({
    tasks,
    expandedTasks,
    setExpandedTasks,
    setEditingTaskId,
    editingElementRef,
    updateTaskText,
    updateTaskStatus,
    updateTaskOrder,
    updateTaskOrders,
    updateTaskParentWithTextAndOrder,
    createTask,
    deleteTask,
    caretAtZeroTaskIdsRef,
    focusFileName,
    arrowKeyCaretPositionRef,
    tabCaretPositionRef,
    pendingFocusRef,
    placeholderStatusRef,
    placeholderDueDateRef,
    placeholderIsLaterDateRef,
    filter,
    visiblePrioritizeSections
  });

  const handleTaskTextClick = useCallback((clientId, e) => {
    // Get click position BEFORE making element contentEditable
    // Prefer .task-text-content when present (Prioritize view with parent) so offset is relative to task text only
    const contentEl = e.target.closest('.task-text-content[data-react-key]');
    const taskTextEl = e.target.closest('.task-text[data-react-key]');
    const clickedElement =
      contentEl && contentEl.getAttribute('data-react-key') === String(clientId)
        ? contentEl
        : taskTextEl && taskTextEl.getAttribute('data-react-key') === String(clientId)
          ? taskTextEl
          : null;
    const clickX = e.clientX;
    const clickY = e.clientY;
    
    // Calculate character offset BEFORE conversion (while element still has HTML)
    let targetCharOffset = null;
    if (clickedElement) {
      targetCharOffset = calculateCaretOffsetFromClick(clickedElement, clickX, clickY);
    }
    
    setEditingTaskId(clientId);
    
    // Store caret positioning data for useLayoutEffect to handle after React renders
    clickCaretPositionRef.current = {
      taskId: clientId, // Keep name as taskId for compatibility
      targetCharOffset,
      isPlaceholder: clientId === 'placeholder_client_key' || clientId === 'placeholder'
    };
  }, [setEditingTaskId, clickCaretPositionRef]);

  const handleTaskContentClick = useCallback((clientId, e) => {
    if (editingTaskId === clientId) return;
    
    const target = e.target;
    const isExcludedElement = 
      target.closest('.expand-button') ||
      target.closest('.checkbox-wrapper') ||
      target.closest('.custom-checkbox') ||
      target.closest('.task-text') ||
      target.closest('.task-due-date') ||
      target.closest('.task-due-date-input') ||
      target.closest('.progress-indicator');
    
    if (isExcludedElement) return;
    
    e.preventDefault();
    const taskTextElement = document.querySelector(`.task-text[data-react-key="${clientId}"]`);
    if (taskTextElement) {
      handleTaskTextClick(clientId, e);
    }
  }, [editingTaskId, handleTaskTextClick]);

  return {
    handleTaskTextClick,
    handleTaskTextFocus,
    handleTaskTextInput,
    handleTaskContentClick,
    handleTaskTextPaste,
    handleTaskTextBlur,
    handleTaskTextKeyDown,
    tryFocusPending,
    focusRequestId
  };
}
